import { getCurrencyFormatter } from "@redotech/money/util";
import { useAnimateHeight } from "@redotech/react-animation/transition";
import { useHandler } from "@redotech/react-util/hook";
import TruckIcon from "@redotech/redo-icons/truck-01.svg";
import { knownScacToLabel } from "@redotech/redo-model/freight/freight";
import type { FreightQuote } from "@redotech/redo-model/freight/freight-quote-request";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoIcon } from "@redotech/redo-web/arbiter-components/icon/redo-icon";
import { RedoRadioButton } from "@redotech/redo-web/arbiter-components/radio/redo-radio-button";
import { Flex } from "@redotech/redo-web/flex";
import { getFreightLogoUrl } from "@redotech/redo-web/freight/logo";
import { Text } from "@redotech/redo-web/text";
import { sentenceCase } from "@redotech/string/transform";
import { memo, useState } from "react";
import * as styles from "./quote-card.module.css";

interface QuoteCardProps {
  quote: FreightQuote;
  selected: boolean;
  onSelect(): void;
  alwaysShowDetails?: boolean;
  selectable?: boolean;
}

export const QuoteCard = memo(function QuoteCard({
  quote,
  selected,
  onSelect,
  alwaysShowDetails,
  selectable = true,
}: QuoteCardProps) {
  const [detailsOpen, setDetailsOpen] = useState(alwaysShowDetails ?? false);
  const [detailsElement, setDetailsElement] = useState<HTMLElement | null>(
    null,
  );
  const [animateStyles, animating] = useAnimateHeight(detailsElement);

  const toggleDetails = useHandler(() => setDetailsOpen((v) => !v));

  const formatter = getCurrencyFormatter(quote.totalPrice.unit);

  const carrierLogoUrl = getFreightLogoUrl(quote.scac);

  const deliveryDays = quote.estDeliveryDays
    ? `${quote.estDeliveryDays} day${quote.estDeliveryDays === 1 ? "" : "s"}`
    : undefined;

  const formattedDeliveryDate = quote.estDeliveryDate
    ? Temporal.PlainDate.from(quote.estDeliveryDate).toLocaleString("en-US", {
        weekday: "short",
        month: "short",
        day: "numeric",
      })
    : undefined;

  const transitText = [
    deliveryDays ? `Est. transit: ${deliveryDays}` : undefined,
    formattedDeliveryDate ? `Delivers ${formattedDeliveryDate}` : undefined,
  ]
    .filter(Boolean)
    .join(" → ");

  return (
    <Flex
      bgColor="primary"
      borderColor={selectable && selected ? "border-alt" : "primary"}
      borderWidth="1px"
      dir="column"
      onClick={selectable ? onSelect : undefined}
      radius="sm"
      style={selectable ? { cursor: "pointer" } : undefined}
    >
      <Flex align="center" gap="xl" p="2xl">
        <div className={styles.carrierIcon}>
          {carrierLogoUrl ? (
            <img
              alt={knownScacToLabel[quote.scac]}
              className={styles.carrierLogo}
              src={carrierLogoUrl}
            />
          ) : (
            <RedoIcon color="secondary" Icon={TruckIcon} size={24} />
          )}
        </div>
        <Flex dir="column" gap="xxs" grow="1">
          <Text fontSize="sm" fontWeight="semibold" textColor="primary">
            {knownScacToLabel[quote.scac]}
          </Text>
          <Text fontSize="sm" textColor="placeholder">
            {sentenceCase(quote.serviceLevel)}
          </Text>
          {transitText && (
            <Text fontSize="xs" textColor="tertiary">
              {transitText}
            </Text>
          )}
        </Flex>
        <Flex align="center" gap="md">
          <Text fontSize="md" fontWeight="semibold" textColor="primary">
            {formatter.format(quote.totalPrice.value)}
          </Text>
          {selectable && (
            <RedoRadioButton
              onSelect={onSelect}
              selected={selected}
              size="sm"
            />
          )}
        </Flex>
      </Flex>
      {!alwaysShowDetails && (
        <Flex pb="lg" pl="2xl">
          <RedoButton
            hierarchy="link_underline"
            onClick={(e) => {
              e.stopPropagation();
              toggleDetails();
            }}
            size="xs"
            text={detailsOpen ? "Hide details" : "Show details"}
          />
        </Flex>
      )}
      <Flex dir="column" ref={setDetailsElement} style={animateStyles}>
        {(animating || detailsOpen) && (
          <Flex
            borderColor="primary"
            borderTopWidth="1px"
            className={styles.detailsSection}
            dir="column"
            gap="sm"
            px="2xl"
            py="xl"
          >
            <Text fontSize="sm" fontWeight="medium" textColor="primary">
              Rate breakdown
            </Text>
            {quote.priceBreakdown.map((item, idx) => {
              const isNegative = item.price.value < 0;
              return (
                <Flex justify="space-between" key={idx}>
                  <Text
                    fontSize="xs"
                    fontWeight="medium"
                    textColor={isNegative ? "success" : "secondary"}
                  >
                    {sentenceCase(item.description)}
                  </Text>
                  <Text
                    fontSize="xs"
                    fontWeight="medium"
                    textColor={isNegative ? "success" : "secondary"}
                  >
                    {isNegative ? "-" : ""}
                    {getCurrencyFormatter(item.price.unit).format(
                      Math.abs(item.price.value),
                    )}
                  </Text>
                </Flex>
              );
            })}
            <Flex
              borderColor="primary"
              borderTopWidth="1px"
              justify="space-between"
              pt="sm"
            >
              <Text fontSize="xs" fontWeight="semibold" textColor="primary">
                Total
              </Text>
              <Text fontSize="xs" fontWeight="semibold" textColor="primary">
                {formatter.format(quote.totalPrice.value)}
              </Text>
            </Flex>
            <Flex dir="column" gap="none" pt="xs">
              <Text fontSize="xxs" textColor="placeholder">
                Quote ID: {quote.quoteId}
              </Text>
              {quote.quoteExpirationDate && (
                <Text fontSize="xxs" textColor="placeholder">
                  Valid until: {quote.quoteExpirationDate}
                </Text>
              )}
            </Flex>
          </Flex>
        )}
      </Flex>
    </Flex>
  );
});
